import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getTextColorForBackground } from "@/utils/color/colorContrast";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import type { Facility } from "@/types";
import { setMapVisibility } from "@/features/map/mapSlice";
import Skeleton from "react-loading-skeleton";

const DEFAULT_CENTER = { lat: 37.0902, lng: -95.7129 };
const DEFAULT_ZOOM = 6;
const MAX_FIT_ZOOM = 14;     // don't allow fitBounds to zoom closer than this
const BOUNDS_PADDING = { top: 60, right: 60, bottom: 60, left: 60 };

interface UnitMarketMapProps {
    facilities: Facility[];
    height: string;
    width: string;
    markerClustering?: boolean;
    pinColor?: string;
}

const createMarkerIconUrl = (color: string, isCluster = false) => {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="${isCluster ? 46 : 22.5}" height="${isCluster ? 46 : 30}" viewBox="0 0 ${isCluster ? 46 : 22.5} ${isCluster ? 46 : 30}">
    <g id="marker" transform="translate(${isCluster ? -291.375 : -1082} ${isCluster ? -594 : -1146.333})">
      <circle id="Ellipse_129" data-name="Ellipse 129" cx="${isCluster ? 16 : 7.5}" cy="${isCluster ? 16 : 7.5}" r="${isCluster ? 16 : 7.5}" transform="translate(${isCluster ? 298.375 : 1086} ${isCluster ? 601 : 1151})" fill="${color}"/>
      ${
          isCluster
              ? `<circle id="Ellipse_138" data-name="Ellipse 138" cx="20" cy="20" r="20" transform="translate(294.375 597)" opacity="0.499" fill="${color}"/>
      <circle id="Ellipse_139" data-name="Ellipse 139" cx="23" cy="23" r="23" transform="translate(291.375 594)" opacity="0.303" fill="${color}"/>`
              : ""
      }
      ${!isCluster ? `<path id="location-dot-sharp-solid" d="M11.25,30S22.5,17.813,22.5,11.25a11.25,11.25,0,0,0-22.5,0C0,17.813,11.25,30,11.25,30Zm0-22.5A3.75,3.75,0,1,1,7.5,11.25,3.75,3.75,0,0,1,11.25,7.5Z" transform="translate(1082 1146.333)" fill="${color}"/>` : ""}
    </g>
  </svg>
`)}`;
};

const createInfoWindowContent = (facility: Facility) => {
    return `<div class="unit-market-infowindow">
    <h4>${facility.name} in ${facility.city}, ${facility.state}</h4>
    <div>
      <p>
        ${facility.address_1}<br />
        ${facility.city}, ${facility.state} ${facility.zipcode}
        <br />
        <a href="tel:${facility.phone}">${facility.phone}</a>
      </p>
      <a class="facility-get-directions" href="//www.google.com/maps/dir//${facility.address_1}, ${facility.city}, ${facility.state}, ${facility.zipcode}">
        Get Directions
      </a>
      <a class="facility-page-link" href="/">
        Rent Units
      </a>
    </div>
  </div>`;
};

const createMarker = (
    options: google.maps.MarkerOptions,
    map: google.maps.Map,
): google.maps.Marker => new google.maps.Marker({ ...options, map });

export const FacilityMap: React.FC<UnitMarketMapProps> = ({
    facilities: filteredFacilities = [],
    height,
    width,
    markerClustering = false,
    pinColor = "#3033e6",
}) => {
    // const { scrollToMap } = useSelector((state: RootState) => state.map);
    const dispatch = useDispatch();
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [, setClusterer] = useState<MarkerClusterer>();
    const prevClusterer = useRef<MarkerClusterer>();
    const [markers, setMarkers] = useState<google.maps.Marker[]>([]); // ?
    const prevMarkersRef = useRef<google.maps.Marker[]>([]);
    const bounds = useRef<google.maps.LatLngBounds>(
        new google.maps.LatLngBounds(),
    );

    useEffect(() => {
        // Function to check and update map visibility based on window width
        const checkMapVisibility = () => {
            dispatch(setMapVisibility(window.innerWidth > 768));
        };
        checkMapVisibility();
        window.addEventListener("resize", checkMapVisibility);
        // Remove event listener on cleanup
        return () => {
            window.removeEventListener("resize", checkMapVisibility);
        };
    }, [dispatch]);

    // Handle Markers and Bounds
    useEffect(() => {
        if (mapRef.current && !map) {
            const newMap = new google.maps.Map(mapRef.current as HTMLElement, {
                gestureHandling: "greedy",
                mapTypeControl: false,
                center: DEFAULT_CENTER,
                zoom: DEFAULT_ZOOM,
            });
            setMap(newMap);
        }
    }, [mapRef, map]);

      // Handle Markers and Bounds
    useEffect(() => {
        if (!map) return;

        // clear prior markers
        clearMarkers(prevMarkersRef.current);
        prevMarkersRef.current = [];

        // no facilities → reset map to default
        if (!filteredFacilities.length) {
            map.setCenter(DEFAULT_CENTER);
            map.setZoom(DEFAULT_ZOOM);
            return;
        }

        const positions: google.maps.LatLng[] = [];
        const newMarkers: google.maps.Marker[] = [];

        bounds.current = new google.maps.LatLngBounds();

        filteredFacilities.forEach((facility, index) => {
            if (facility.latitude == null || facility.longitude == null) {
                console.warn("Skipping facility with missing lat/lng:", facility?.name);
                return;
            }
            const position = new google.maps.LatLng(
                Number(facility.latitude),
                Number(facility.longitude)
            );
            positions.push(position);
            bounds.current.extend(position);

            const marker = createMarker(
                {
                    map,
                    position,
                    label: {
                        text: (index + 1).toString(),
                        color: getTextColorForBackground(pinColor),
                    },
                    icon: markerIcon(pinColor),
                },
                map
            );

            const infoWindow = new google.maps.InfoWindow({
                content: createInfoWindowContent(facility),
            });
            marker.addListener("click", () => infoWindow.open(map, marker));

            prevMarkersRef.current.push(marker);
            newMarkers.push(marker);
        });

        // If only one marker, choose a sane zoom instead of fitBounds’ super close zoom
        if (positions.length === 1) {
            map.setCenter(positions[0]);
            map.setZoom(12); // tweak this to taste
        } else {
            // Use padding, then clamp the zoom once after bounds settle
            map.fitBounds(bounds.current, BOUNDS_PADDING as google.maps.Padding);
            google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
            });
        }

        setMarkers(newMarkers);
    }, [map, filteredFacilities, pinColor]);

    // Handle Cluster
    useEffect(() => {
        if (markerClustering && map && markers.length > 0) {
            // clear previous clusterer
            prevClusterer.current?.setMap(null);
            const newClusterer = new MarkerClusterer({
                markers: markers,
                map: map,
                renderer: {
                    render: ({ count, position }) =>
                        createMarker(
                            {
                                position,
                                icon: markerIcon(pinColor, true),
                                label: {
                                    text: count.toString(),
                                    className: "map-cluster-label",
                                    color: getTextColorForBackground(pinColor),
                                },
                                zIndex: google.maps.Marker.MAX_ZINDEX + count,
                            },
                            map,
                        ),
                },
            });
            prevClusterer.current = newClusterer;
            setClusterer(newClusterer);
        }
    }, [markerClustering, markers, map, pinColor, filteredFacilities]);

    const markerIcon = (color: string, isCluster = false) => ({
        url: createMarkerIconUrl(color, isCluster),
    });

    const clearMarkers = (markers: google.maps.Marker[]) => {
        for (const marker of markers) {
            marker.setMap(null);
        }
    };

    if (!filteredFacilities.length) {
        return <Skeleton height={height} width={width} />;
    }

    return (
        <div
            id="nearbyUnitMapBox"
            style={{ height, width }}
            ref={mapRef}
        ></div>
    );
};
